import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useAudioRecorder } from "react-audio-voice-recorder";
import Draggable from "react-draggable";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import MainWithoutPadding from "../components/MainWithoutPadding";
import { soapApi } from "../redux/actions/api";
import {
  postClinicalNotesWoLoader,
  setApiInProgress,
} from "../redux/reducers/clinicSlice";
import { hideLoader, showLoaderText } from "../redux/reducers/loaderSlice";
import { getPetsCompliantSummaryWoLoader } from "../redux/reducers/petSlice";
import {
  setAudioBlobData,
  setAudRecordingVisbility,
  setAudSaved,
  setSoapNoteDetails,
  setSoapNoteType,
} from "../redux/reducers/recorderSlice";
import { showSnackBar } from "../redux/reducers/snackSlice";
import { AppColors } from "../util/AppColors";
import { getArryAsString, parseSoapNote } from "../util/function";
import Header from "./header";

const StyledRoot = styled("div")({
  display: "flex",
  minHeight: "100%",
  overflow: "hidden",
});

const CustomLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const recorder = useAudioRecorder();
  const [isMinView, setMinView] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const { appointment, bill } = location?.state || {};
  const { visible, type } = useSelector((state) => state?.recorder);
  const accessToken = localStorage.getItem("accessToken");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);
  const petDetailsForSoap = useSelector(
    (state) => state?.pet?.petDetailsForSoap
  );
  const { details: clinicDet } = useSelector((state) => state?.clinic);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const apntId = reqDet?.appointment?.appointmentId;
  const apnt = reqDet?.appointment;
  const petDet = reqDet?.pet;

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = () => {
    if (accessToken !== null) {
      // Only redirect to dashboard if user is on the root path
      const currentPath = location.pathname;
      if (currentPath === "/" || currentPath === "") {
        return navigate("/dashboard");
      }
      // For all other paths, don't redirect - let the router handle it
      return;
    } else {
      // If no token, redirect to login
      return navigate("/login");
    }
  };

  const onUpdateNote = ({
    Subjective = "",
    Objective = "",
    Assessment = "",
    Plan = "",
    Conclusion = "",
    DifferentialDiagnosis = "",
    KeyIssues = "",
    AbnormalFindings = "",
    CustomerInstructions = "",
    Reminders = "",
    sessionId = "",
    transcript = "",
    modificationCount = 0,
    blob = null,
    type = "outpatient",
    Summary = "",
    InstructionsStatus = "",
    RedFlags = "",
    OtherUpdates = "",
    Observation = "",
    consolidated = false,
    Protocols = "",
    ProgressNotes = "",
    PreSurgicalDocumentation = "",
    IntraSurgicalDocumentation = "",
    PostSurgicalDocumentation = "",
    OwnerCommunication = "",
    AdditionalDocumentation = "",
  }) => {
    const form = new FormData();
    form.append("subjective", Subjective);
    form.append("objective", Objective);
    form.append("assessment", Assessment);
    form.append("plan", Plan);
    form.append("conclusion", Conclusion);
    form.append("tag", DifferentialDiagnosis);
    form.append("keyIssues", KeyIssues);
    form.append("abnormalFindings", AbnormalFindings);
    form.append("customerInstructions", CustomerInstructions);
    form.append("reminders", Reminders);
    form.append("summary", Summary);
    form.append("instructionsStatus", InstructionsStatus);
    form.append("redFlags", RedFlags);
    form.append("otherUpdates", OtherUpdates);
    form.append("observation", Observation);
    form.append("sessionId", sessionId);
    form.append("modificationCount", modificationCount);
    form.append("type", type);
    form.append("consolidated", consolidated);
    form.append("preSurgicalDocumentation", PreSurgicalDocumentation);
    form.append("intraSurgicalDocumentation", IntraSurgicalDocumentation);
    form.append("postSurgicalDocumentation", PostSurgicalDocumentation);
    form.append("ownerCommunication", OwnerCommunication);
    form.append("additionalDocumentation", AdditionalDocumentation);
    if (consolidated) {
      form.append("protocols", Protocols);
      form.append("progressNotes", ProgressNotes);
    }
    form.append("transcript", transcript);

    if (blob) {
      form.append("objectFile", blob, "recording.webm");
    }
    dispatch(postClinicalNotesWoLoader({ appId: apntId, form })).then((res) => {
      if (res?.payload) {
        // setSelectedNote(consolidated ? null : res?.payload);
        dispatch(setSoapNoteDetails(null));
        dispatch(setAudSaved(true));
        dispatch(setAudRecordingVisbility(false));
        dispatch(setAudioBlobData(null));
        dispatch(getPetsCompliantSummaryWoLoader(apntId));
        dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            type: "success",
          })
        );
      }
    });
  };

  const getApptDetailsForSoap = () => {
    const reqObj = {
      petName: petDet?.petName ?? apnt?.petName ?? "",
      species: petDet?.petType ?? "",
      breed: petDet?.breed ?? apnt?.breed ?? "",
      age: petDet?.dob ?? apnt?.dob ?? "",
      reproductiveStatus: petDet?.reproductiveStatus ?? "",
      gender: petDet?.gender ?? apnt?.gender ?? "",
      weight: petDet?.weight ?? apnt?.weight ?? "",
      appointmentDateTime: apnt?.appointmentdateTime ?? "",
      bloodGroup: petDet?.bloodGroup ?? apnt?.bloodGroup ?? "",
      temperament: petDet?.temperament ?? apnt?.temperament ?? "",
      color: petDet?.color ?? apnt?.color ?? "",
      rfid: petDet?.rfid ?? apnt?.rfid ?? "",
      veterinaryClinic: clinicDet?.name ?? "",
      username: petDet?.userName ?? apnt?.userName ?? "",
      vetName: apnt?.doctorName ?? "",
      appointmentId: apnt?.appointmentId ?? "",
    };
    return JSON.stringify(reqObj);
  };

  const onStopAudio = (blob) => {
    // dispatch(showLoader());
    // dispatch(
    //   showLoaderText(
    //     `Please wait while we generate the ${
    //       type === "inpatient"
    //         ? "Daily updates"
    //         : type === "surgery"
    //         ? "Surgery SOAP notes"
    //         : "SOAP notes"
    //     }`
    //   )
    // );
    try {
      const form = new FormData();
      form.append("audio", blob, "recording.webm");
      form.append("case_type", type ?? "outpatient");
      if (type === "inpatient") {
        const combRedFlags = reqDet?.inpatientNotes
          ?.filter(
            (li) =>
              li?.appointmentId === reqDet?.appointment?.appointmentId &&
              li?.type === "inpatient"
          )
          ?.map((li) => li?.redFlags)
          ?.join(", ");
        const combInstFlags = reqDet?.inpatientNotes
          ?.filter(
            (li) =>
              li?.appointmentId === reqDet?.appointment?.appointmentId &&
              li?.type === "inpatient"
          )
          ?.map((li) => li?.instructionsStatus)
          ?.join(", ");
        if (combRedFlags?.length > 0) {
          form.append("RedFlags", combRedFlags);
        }
        if (combInstFlags?.length > 0) {
          form.append("Instructions", combInstFlags);
        }
      }
      form.append("appt_details", getApptDetailsForSoap());
      dispatch(setAudRecordingVisbility(false));
      dispatch(
        setApiInProgress({ apiType: "soapGeneration", inProgress: true })
      );
      soapApi.post("/generate-soap-note/", form).then((res) => {
        dispatch(
          setApiInProgress({ apiType: "soapGeneration", inProgress: false })
        );
        if (res?.data?.soap_note) {
          dispatch(setAudSaved(false));
          const apiRes = res?.data?.soap_note;
          const reqObj = parseSoapNote(apiRes);
          const secObj = {
            ...reqObj,
            transcript: reqObj?.Transcript ?? res?.data?.cleaned_transcription,
            sessionId: res?.data?.session_id,
            modificationCount: 0,
            blob,
            type: type ?? "outpatient",
          };
          if (type === "surgery") {
            secObj.PreSurgicalDocumentation = Array.isArray(
              reqObj?.PreSurgicalDocumentation
            )
              ? getArryAsString(reqObj?.PreSurgicalDocumentation ?? [])
              : "";
            secObj.IntraSurgicalDocumentation = Array.isArray(
              reqObj?.IntraSurgicalDocumentation
            )
              ? getArryAsString(reqObj?.IntraSurgicalDocumentation ?? [])
              : "";
            secObj.PostSurgicalDocumentation = Array.isArray(
              reqObj?.PostSurgicalDocumentation
            )
              ? getArryAsString(reqObj?.PostSurgicalDocumentation ?? [])
              : "";
            secObj.OwnerCommunication = Array.isArray(
              reqObj?.OwnerCommunication
            )
              ? getArryAsString(reqObj?.OwnerCommunication ?? [])
              : "";
            secObj.AdditionalDocumentation = Array.isArray(
              reqObj?.AdditionalDocumentation
            )
              ? getArryAsString(reqObj?.AdditionalDocumentation ?? [])
              : "";
          }
          onUpdateNote(secObj);
          // dispatch(
          //   setSoapNoteDetails({
          //     ...reqObj,
          //     transcript: res?.data?.cleaned_transcription,
          //     sessionId: res?.data?.session_id,
          //     modificationCount: 0,
          //     blob,
          //   })
          // );
          // const storedAppointment = localStorage.getItem("appointment");
          // navigate(`/clinic-pet-details`, {
          //   state: {
          //     appointment: appointment ?? JSON.parse(storedAppointment),
          //     bill,
          //     selectedTab: "patientWorkbook",
          //     open: "soapNote",
          //   },
          // });
          dispatch(setAudRecordingVisbility(false));
          setIsRecording(false);
          dispatch(hideLoader());
          dispatch(showLoaderText(""));
        } else {
          dispatch(hideLoader());
          dispatch(showLoaderText(""));
        }
      });
    } catch (err) {
      dispatch(
        setApiInProgress({ apiType: "soapGeneration", inProgress: false })
      );
      dispatch(hideLoader());
      dispatch(showLoaderText(""));
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startTimer = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      setTime((prev) => (!isPaused ? prev + 1 : prev));
    }, 1000);
    setIsRunning(true);
  };

  const pauseTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIsRunning(false);
  };

  const resumeTimer = () => {
    if (intervalRef.current) return; // Already running
    intervalRef.current = setInterval(() => {
      setTime((prev) => prev + 1);
    }, 1000);
    setIsRunning(true);
  };

  const stopTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setTime(0);
    setIsRunning(false);
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    chunksRef.current = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      await onStopAudio(blob);
    };

    mediaRecorder.start();
    setIsRecording(true);
    startTimer();
  };

  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      pauseTimer();
    }
  };

  const resumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      resumeTimer();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    setIsRecording(false);
    stopTimer();
  };

  // const addAudioElement = async (blob) => {
  //   setAudioBlob(blob);
  //   dispatch(setAudioBlobData(blob));
  //   const url = URL.createObjectURL(blob);
  //   const audio = document.createElement("audio");
  //   audio.src = url;
  //   audio.controls = true;
  //   document.body.appendChild(audio);
  //   const storedAppointment = await localStorage.getItem("appointment");
  //   navigate(`/clinic-pet-details`, {
  //     state: {
  //       appointment: appointment ?? JSON.parse(storedAppointment),
  //       bill,
  //       selectedTab: "patientWorkbook",
  //     },
  //   });
  //   dispatch(setAudRecordingVisbility(false));
  // };

  const onCloseRecord = () => {
    dispatch(setAudRecordingVisbility(false));
    dispatch(setSoapNoteType("outpatient"));
  };

  return (
    <StyledRoot>
      <MainWithoutPadding>
        <Header name="VetInstant" />
        <Outlet />
        {visible &&
          (isMinView ? (
            <Draggable>
              <div
                className="flex-column p5"
                style={{
                  borderRadius: 5,
                  position: "absolute",
                  right: 0,
                  top: 0,
                  alignItems: "center",
                  background: "#FFC225",
                }}
              >
                <Typography className="white-color fs10 txt-semi-bold">
                  {`${
                    type === "inpatient"
                      ? "Daily Update"
                      : type === "surgery"
                      ? "Surgery SOAP"
                      : "SOAP"
                  } Recording`}
                </Typography>
                <img
                  src={require("./../assets/images/png/maxiView.png")}
                  alt=""
                  style={{
                    width: 20,
                    height: 20,
                  }}
                  className="cursor mt10"
                  onClick={() => {
                    setMinView(false);
                  }}
                />
              </div>
            </Draggable>
          ) : (
            <div
              className="flex-column p15"
              style={{
                borderRadius: 5,
                position: "absolute",
                right: 0,
                top: 0,
                width: "25%",
                background: "#FFC225",
                height: "100%",
                zIndex: 100,
              }}
            >
              <img
                src={require("./../assets/images/png/minView.png")}
                alt=""
                style={{
                  width: 20,
                  height: 20,
                  position: "absolute",
                  right: 70,
                }}
                className="cursor"
                onClick={() => setMinView(true)}
              />

              <img
                src={require("./../assets/images/png/closeWhite.png")}
                alt=""
                style={{
                  width: 20,
                  height: 20,
                  position: "absolute",
                  right: 30,
                }}
                className="cursor"
                onClick={onCloseRecord}
              />

              <Tooltip title="SOAP Note">
                <div className="flex-row-ali-cen mt20">
                  <Typography className="font-bold mar̦k-balck fs18">
                    {`${
                      type === "inpatient"
                        ? "Daily Update"
                        : type === "surgery"
                        ? "Surgery SOAP"
                        : "SOAP"
                    } Recording`}
                  </Typography>
                  <img
                    src={require("./../assets/images/png/micBlack.png")}
                    alt=""
                    className="img-wh20 ml5"
                  />
                </div>
                {!isRecording && !isPaused ? (
                  <div className="flex-center mt30">
                    <div
                      className={`blu-back flex-center wh50 br50Per ${
                        isRecording ? "no-cursor" : "cursor"
                      }`}
                      onClick={startRecording}
                    >
                      <PlayArrowIcon
                        sx={{ width: 30, height: 30, color: AppColors.white }}
                      />
                    </div>
                  </div>
                ) : null}

                {isRecording ? (
                  <div>
                    {!isPaused ? (
                      <div className="flex-center mt30">
                        <div
                          className={`blu-back flex-center wh50 br50Per cursor`}
                          onClick={pauseRecording}
                        >
                          <PauseIcon
                            sx={{
                              width: 30,
                              height: 30,
                              color: AppColors.white,
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex-center mt30">
                        <div
                          className={`blu-back flex-center wh50 br50Per cursor`}
                          onClick={resumeRecording}
                        >
                          <PlayArrowIcon
                            sx={{
                              width: 30,
                              height: 30,
                              color: AppColors.white,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div
                      className={`flex-center mt20 ${
                        !isRecording ? "no-cursor" : "cursor"
                      }`}
                      onClick={stopRecording}
                    >
                      <div className="stp-rng">
                        <div className="stp-dot" />
                      </div>
                    </div>

                    <Typography className="mt20 flex-center txt-semi-bold fs14">
                      {formatTime(time)}
                    </Typography>
                  </div>
                ) : null}

                {/* <div className="mt50 flex-center">
                  <AudioRecorder
                    recorder={recorder}
                    onRecordingComplete={addAudioElement}
                    audioTrackConstraints={{
                      noiseSuppression: true,
                      echoCancellation: true,
                    }}
                    showVisualizer={true}
                    downloadFileExtension="mp3"
                    start
                  />
                </div> */}
              </Tooltip>
            </div>
          ))}
      </MainWithoutPadding>
    </StyledRoot>
  );
};

export default CustomLayout;
